import { useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { subDays } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { displayToastSuccess } from '@/helpers/display-toast';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import {
  invalidateTimeTrackerProjectsQueries,
  invalidateTimeTrackingQueries,
} from '@/modules/common/utils/query-invalidation';
import { formatDateToISO } from '@/utils/date-formatters';

import { cutAssignment } from '../../api/assignments.api';
import {
  applyOptimisticMemberUpdate,
  ASSIGNMENT_QUERY_KEYS,
  cancelAssignmentQueries,
  computeActivePeriods,
  findAssignmentInCache,
  OptimisticContext,
  rollbackAssignmentData,
  snapshotAssignmentData,
} from '../../helpers/assignment-cache-utils';
import {
  CutAssignmentWithUIRequest,
  PlannerAssignmentResponse,
  PlannerMemberResponse,
} from '../../types/dto';

/**
 * Hook for cutting an assignment into two parts with optimistic updates.
 * Immediately shows the split assignments in the UI before the API call completes.
 * Automatically invalidates only the visible date range queries on success.
 *
 * @param options - Optional TanStack Query mutation options
 * @returns Mutation object with mutate, isPending, etc.
 *
 * @example
 * ```typescript
 * const cutMutation = useCutAssignment({
 *   onSuccess: () => logger.debug(TAG, 'Assignment cut!')
 * });
 *
 * cutMutation.mutate({
 *   assignmentId: 123,
 *   cutDate: '2025-08-22',
 *   syncToken: 'abc123'
 * });
 * ```
 */
export function useCutAssignment(
  options?: UseMutationOptions<void, Error, CutAssignmentWithUIRequest>
) {
  const queryClient = useQueryClient();
  const { t } = useTranslation('authenticated.planner');
  const { displayToastErrorFromApiWithFallback } = useApiError();

  const { onSuccess, onError, ...restOptions } = options ?? {};

  return useMutation({
    ...restOptions,
    mutationFn: (request: CutAssignmentWithUIRequest) => cutAssignment(request.payload),
    onMutate: async (variables: CutAssignmentWithUIRequest): Promise<OptimisticContext> => {
      const { payload } = variables;

      // Cancel outgoing refetches to prevent overwriting optimistic update
      await cancelAssignmentQueries(queryClient);

      // Snapshot previous values for rollback
      const previousData = snapshotAssignmentData(queryClient);

      // Find the assignment's current dates and owner for utilization recalculation
      const cachedAssignment = findAssignmentInCache(queryClient, payload.assignmentId);
      const oldAssignmentDates = cachedAssignment?.dates;
      const assignmentUserId = cachedAssignment?.userId;

      // Helper to split assignment in a member
      const cutAssignmentInMember = (member: PlannerMemberResponse): PlannerMemberResponse => {
        const updatedAssignments = member.assignments.flatMap(plannerAssignment => {
          if (plannerAssignment.assignment.id !== payload.assignmentId) {
            return plannerAssignment;
          }

          const originalAssignment = plannerAssignment.assignment;

          // Calculate the end date for the first part (one day before cutDate)
          const firstPartEndDate = formatDateToISO(subDays(new Date(payload.cutDate), 1));

          // Preserve the last known rate for optimistic period generation
          const lastKnownRate = plannerAssignment.activePeriods[0]?.rate ?? null;

          // Create first part: original startDate to (cutDate - 1)
          const firstPartAssignment: PlannerAssignmentResponse = {
            ...plannerAssignment,
            // Each half's total is unknown until the refresh; don't inherit the original's
            bookedMinutes: null,
            assignment: {
              ...originalAssignment,
              id: -Date.now(), // Temporary negative ID
              endDate: firstPartEndDate,
            },
            activePeriods: computeActivePeriods(
              originalAssignment.startDate,
              firstPartEndDate,
              originalAssignment.doSkipWeekdays,
              lastKnownRate
            ),
          };

          // Create second part: cutDate to original endDate
          const secondPartAssignment: PlannerAssignmentResponse = {
            ...plannerAssignment,
            bookedMinutes: null,
            assignment: {
              ...originalAssignment,
              id: -Date.now() - 1, // Different temporary negative ID
              startDate: payload.cutDate,
            },
            activePeriods: computeActivePeriods(
              payload.cutDate,
              originalAssignment.endDate,
              originalAssignment.doSkipWeekdays,
              lastKnownRate
            ),
          };

          return [firstPartAssignment, secondPartAssignment];
        });

        return {
          ...member,
          assignments: updatedAssignments,
        };
      };

      // Optimistically update all three cache views (byMember, byDepartment, byProject)
      applyOptimisticMemberUpdate(queryClient, cutAssignmentInMember);

      return { ...previousData, oldAssignmentDates, assignmentUserId };
    },
    onSuccess: (data, variables, context) => {
      // Backend doesn't return IDs for both new assignments after cut,
      // so we must invalidate all pages to get the real data.
      queryClient.invalidateQueries({ queryKey: ASSIGNMENT_QUERY_KEYS.byMember });
      queryClient.invalidateQueries({ queryKey: ASSIGNMENT_QUERY_KEYS.byDepartment });
      queryClient.invalidateQueries({ queryKey: ASSIGNMENT_QUERY_KEYS.byProject });
      // Assignment changes affect booked hours shown on the time tracking page
      invalidateTimeTrackingQueries(queryClient);
      // Assignment changes can flip which projects count as "booked" in the
      // Time Tracking modal's project picker.
      invalidateTimeTrackerProjectsQueries(queryClient);

      displayToastSuccess(
        t('toasts.assignmentCutSuccess', {
          userName: variables.ui?.userName ?? '',
          projectName: variables.ui?.projectName ?? '',
        })
      );

      onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      // Rollback optimistic updates on error
      rollbackAssignmentData(queryClient, context as OptimisticContext);

      displayToastErrorFromApiWithFallback(
        error,
        'authenticated.planner:toasts.cutAssignmentError',
        {
          interpolation: {
            userName: variables.ui?.userName ?? '',
            projectName: variables.ui?.projectName ?? '',
          },
        }
      );

      onError?.(error, variables, context);
    },
  });
}
